/**
 * grantAxis — the grant page's grouping engine (F2b).
 *
 * The preview endpoint returns a FLAT list of (resource, verb, schema) triples.
 * A page that rendered one row per triple would be unreadable at 22 grants, and
 * every row would look equally serious. This turns the list into cards.
 *
 * TWO RULES, IN ORDER.
 *
 *   1. Bucket by consequence. Buckets never reorder — "Atlas can delete your
 *      notes" must never sort below a read because `nodes` came first
 *      alphabetically.
 *
 *   2. The SCHEMA is the card and every verb on it is a chip. Collapsed once
 *      for the whole page, then filed under the bucket of its worst verb — so a
 *      schema is named exactly once, wherever its permissions fall.
 *
 * WHY SEVERITY IS KEYED ON THE VERB. Delete outranks read for everyone. Whether
 * Health outranks Notes depends on the person, so the system sorts verbs and
 * never presumes to sort schemas.
 *
 * WHY UNKNOWN PAIRS SURVIVE. An unrecognised (resource, verb) lands in its own
 * bucket rather than being filtered out. A permission the page silently drops is
 * a permission the user grants without seeing — the failure mode that produced
 * `relationships:inherit` and the `edit_content` fold bug. Unrecognised is a
 * question, not garbage.
 */

import { RESOURCE_LABEL } from '../../utils/realmReach';

/** severity rank (lower = more consequential) + the bucket it belongs to. */
export const VERB_META = {
  // `scoped` means the own/any toggle applies. It is not about danger — it is
  // about whether the verb acts on entries that ALREADY EXIST, because that is
  // the only thing "only what it made" can filter. The backend enforces it in
  // `_scope_to_query`, which both filter_readable_nodes and filter_writable_nodes
  // run through: nothing there is write-specific. Marking only the destructive
  // verbs left READ — the widest reach an app ever has, and the one place the
  // distinction matters most — with no control at all.
  //
  // The test is ENFORCEMENT, not grammar — and enforcement must read REAL data.
  // `create` was scoped for one day (2026-09-02): its "own" meant "schemas this
  // app defined", checked against a column the CLI push flow never writes, so
  // apps were refused their own shapes and every create 403'd (Caliber,
  // Missions). See SCOPED_VERBS in routes/third_party/preview.py — the parity
  // test holds both sides in step.
  //
  //   read · edit · delete            reach nodes through filter_readable_nodes
  //                                   / filter_writable_nodes — enforced
  //   create                          no query; the entry does not exist yet
  //   propose                         capability only — proposal_permission()
  //                                   checks has_verb and the route never
  //                                   filters the target node
  //
  // `propose` was briefly marked scoped because it grammatically acts on an
  // existing entry. It does, but nothing checks WHICH entry, so the toggle
  // would have been stored, displayed and never consulted — the same defect as
  // a friend grant carrying `plugins:execute`.
  'nodes:delete':      { sev: 1, bucket: 'graph', tone: 'delete',  scoped: true },
  'nodes:edit':        { sev: 2, bucket: 'graph', tone: 'edit',    scoped: true },
  'nodes:create':      { sev: 3, bucket: 'graph', tone: 'create',  scoped: false },
  'nodes:propose':     { sev: 4, bucket: 'graph', tone: 'propose', scoped: true },
  'nodes:read':        { sev: 9, bucket: 'graph', tone: 'read',    scoped: true },
  'activity:read':     { sev: 9, bucket: 'runs',  tone: 'read',    scoped: false },
  'discovery:friends': { sev: 9, bucket: 'runs',  tone: 'read',    scoped: false },
  'discovery:groups':  { sev: 9, bucket: 'runs',  tone: 'read',    scoped: false },
  'discovery:realms':  { sev: 9, bucket: 'runs',  tone: 'read',    scoped: false },
  'plugins:execute':   { sev: 9, bucket: 'runs',     tone: 'run',     scoped: false },
  'automations:write': { sev: 5, bucket: 'runs',     tone: 'run',     scoped: false },
  'automations:execute': { sev: 5, bucket: 'runs',   tone: 'run',     scoped: false },
  'sub_entity:declare':{ sev: 5, bucket: 'runs',     tone: 'run',     scoped: false },
};

/** Buckets, in render order. Order is normative — never sort these. */
/**
 * ONE SECTION PER AXIS, NOT PER VERB.
 *
 * A schema is a thing in your graph; what an app may do to it is a property of
 * that thing, carried by the chips on its card. Splitting by verb put `concept`
 * under "Reads only" and `relationship` under "Changes your graph directly" —
 * the same noun in two places, with the reader left to reassemble it. Worse,
 * a schema granted read AND delete could only be filed once, so one of its
 * verbs decided where the whole card lived.
 *
 * `nodes` is the only resource with a schema axis. Everything else — discovery,
 * plugins, automations, sub-entity — has no noun in your graph at all and is
 * shown separately as capabilities. That is the real seam, and it is a fact
 * about the resource, not about how dangerous the verb happens to be.
 */
/**
 * CONNECTIONS ARE NOT A PEER OF THE THINGS THEY CONNECT.
 *
 * Since relationships became nodes, `relationship` and `belongs_to` are ordinary
 * schemas — so they sorted in among `goal` and `habit` by verb severity, as if
 * "read your connections" were the same KIND of request as "read your goals".
 * It is not, in two ways the page has to carry:
 *
 *   * A relationship has no meaning without its endpoints. Asked for on its
 *     own it reads as a mystery — the reader has no way to tell what it would
 *     actually expose.
 *   * It cannot reach past what you granted above. `authz/referents.py` drops
 *     any node whose declared node-ref fields point outside the reader's
 *     readable set, so a link is visible only when BOTH ends are. That is a
 *     fact about the grant the reader should be told, and it is only sayable
 *     once connections are rendered as dependent on the schemas above them.
 *
 * Hence a tier of its own, rendered after the content it depends on.
 *
 * KEYED ON `connects_nodes`, NOT `references_nodes`. The tier used to test the
 * bare `node_ref_fields` column, which says a schema HOLDS node ids — true of
 * content schemas too, and increasingly so: a goal pointing at its parent, or
 * anything pointing at a relationship node. Those are nodes that HAVE
 * connections, not nodes that ARE one, and filing them here described them with
 * a note about the nodes "they connect" that made no sense for them.
 *
 * `connects_nodes` is the schema's own `is_relationship` declaration (migration
 * 231) — set by whoever declares the schema, which is the only party that knows.
 * The referent RULE is unchanged and still keys on node_ref_fields for every
 * schema that has them: this narrows what the page groups and annotates, never
 * what the gate enforces.
 */
export const BUCKETS = [
  { id: 'graph',       title: 'Identity Graph Nodes' },
  { id: 'connections', title: 'How they connect',
    // A CONTINUATION, NOT A SECTION. These rows are still nodes with still the
    // same verb columns. Given its own all-caps eyebrow the tier cut as hard as
    // "Other access", which IS a category change (it leaves nodes entirely), and
    // the page read as three peers instead of one list with a qualified tail.
    // So: no heading, and the grouping survives only as the ORDER — content
    // first, then the rows that depend on it.
    continues: true,
    // NO GROUP NOTE. The rule moved onto the rows (see SchemaCard), because it
    // was never one rule: `relationship` is shared when the nodes it connects
    // are, `belongs_to` turns on its `to` end alone — so a single sentence over
    // both described one of them and misdescribed the other.
  },
  { id: 'runs',        title: 'Actions' },
  { id: 'other',       title: 'Not recognised' },
];

/** A grant over a schema whose PURPOSE is joining two other nodes.
 *
 *  Not the same as holding node ids — see the tier note above. `references_nodes`
 *  is still on the wire and still true of content schemas with node refs; it is
 *  deliberately not what this tests.
 */
export const connectsNodes = (g) => g.resource === 'nodes' && !!g.connects_nodes;

const UNKNOWN = { sev: 6, bucket: 'other', tone: 'read', scoped: false };

/** What a resource is called when it has no schema axis at all.
 *
 *  `discovery:friends` does not grant "everything" — it grants seeing who you
 *  know. Only `nodes` has schemas, so every other resource arrived with
 *  schema_id null, got swept into the same '*' bucket, and was labelled
 *  "Everything": three unrelated permissions under a word that overstates all
 *  of them.
 *
 *  THE NAMES COME FROM `utils/realmReach`, not from here. That map already
 *  names every resource for the realm editor, and the product says
 *  "Sub-entities" where a first draft of this file said "Agents" — a second
 *  vocabulary for the same six things is how an interface stops being one
 *  interface. `nodes` is the exception: an unrestricted nodes grant really does
 *  mean every schema, which is a statement about scope rather than the
 *  resource's name.
 */
export const RESOURCE_LABELS = { ...RESOURCE_LABEL, nodes: 'Everything' };
const labelForResource = (r) => RESOURCE_LABELS[r] || r;

export const keyOf = (g) => `${g.resource}:${g.verb}`;
export const metaOf = (g) => VERB_META[keyOf(g)] || UNKNOWN;

/** Verbs whose scope_type decides own-vs-any.
 *
 *  THE SERVER DECIDES, AND IT SAYS FOUR: `SCOPED_VERBS` in
 *  routes/third_party/preview.py is {read, edit, delete, propose}, and each ask
 *  row arrives carrying `scoped`. This note used to claim the set was {edit,
 *  delete} and that `read` was deliberately left out — it was not, and the
 *  control has been naming read in its sentence all along. A comment that
 *  contradicts the wire is worse than none: the next person to "restore" the
 *  smaller set would narrow a shipped control.
 *
 *  `create` is the one that cannot be here, and not by choice: `scope_type:
 *  own` is enforced as a filter over EXISTING rows (`created_by_third_party_id
 *  == this app`), and everything an app creates is its own by definition. On
 *  create the restriction would promise something it does not do.
 *  Stored per VERB and spanning every schema that verb touches — which is why
 *  the control is asked once per bucket, not once per card. A per-card control
 *  would promise precision the model cannot store. */
export const isScoped = (g) => metaOf(g).scoped;

const bySev = (a, b) => a.sev - b.sev || a.key.localeCompare(b.key);

function collapse(grants, axis) {
  const map = new Map();
  for (const g of grants) {
    // A grant with no schema is keyed by its RESOURCE, not lumped with every
    // other schemaless grant. `plugins:execute` and `discovery:friends` share
    // nothing except the absence of a schema.
    const k = axis === 'schema'
      ? (g.all_schemas ? `res:${g.resource}` : String(g.schema_id))
      : keyOf(g);
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(g);
  }
  return [...map.entries()].map(([key, items]) => ({
    key,
    axis,
    schemaless: axis === 'schema' && key.startsWith('res:'),
    label: axis === 'schema'
      ? (key.startsWith('res:')
          ? labelForResource(items[0].resource)
          : (items[0].schema_name || 'Unknown'))
      : items[0].verb,
    grants: items,
    // `belongs_to` WIDENS — the from-node inherits the audience of the to-node.
    // Every other connection only ever narrows, so this one cannot sit silently
    // under a note about what stays invisible; the card says so itself.
    inheritsAudience: items.some((g) => g.inherits_audience),
    // The card needs this too, not just the bucketing pass: each connector row
    // now carries its own rule, so the row has to know it is one.
    connectsNodes: items.some(connectsNodes),
    // A card's severity is the most consequential verb it CARRIES. Callers narrow
    // this to enabled grants so the card calms as the user declines.
    sev: Math.min(...items.map((g) => metaOf(g).sev)),
  })).sort(bySev);
}

/**
 * Group a flat grant list into rendered buckets.
 *
 * @param {Array} grants  flat (resource, verb, schema) triples from /authorize/preview
 * @returns {Array} [{ id, title, axis, cards, scopedVerbs }]
 */
export function groupGrants(grants = []) {
  // ONE CARD PER SCHEMA, ACROSS THE WHOLE PAGE.
  //
  // Bucketing first and collapsing second split a schema across sections: a
  // `belongs_to` you can create, delete AND read appeared under "Changes your
  // graph" for two of those verbs and again under "Reads only" for the third.
  // Same noun, twice, with its permissions divided between two places — the
  // third time this duplication has surfaced, and the last axis it had left.
  //
  // So the schema is collapsed ONCE, globally, and the finished card is then
  // filed under the bucket of its most consequential verb. That also sharpens
  // what the sections mean: "Reads only" now holds the schemas this app can
  // ONLY read, which is a real statement rather than a list of read grants.
  const cards = collapse(grants, 'schema');

  const byBucket = new Map();
  for (const card of cards) {
    // THE TIER WINS OVER SEVERITY. A card whose schema references other nodes
    // belongs with the connections regardless of which verb it carries —
    // otherwise `belongs_to` with a create would file under "Your graph" and
    // `relationship` with only a read would file elsewhere again, splitting the
    // one idea across the page exactly as the schema/verb axes used to.
    const id = card.grants.some(connectsNodes)
      ? 'connections'
      : metaOf(card.grants.reduce((a, g) => (metaOf(g).sev < metaOf(a).sev ? g : a))).bucket;
    if (!byBucket.has(id)) byBucket.set(id, []);
    byBucket.get(id).push(card);
  }

  const out = [];
  for (const bucket of BUCKETS) {
    const inBucket = byBucket.get(bucket.id);
    if (!inBucket?.length) continue;
    inBucket.sort(bySev);
    out.push({
      // SPREAD, not a hand-copied field list. Every presentation field declared
      // in BUCKETS has to survive the trip, and the list copied `id/title/note`
      // by hand — so `continues` was declared, read by <Bucket>, and silently
      // dropped in between. A field added there must not need an edit here.
      ...bucket,
      axis: 'schema',
      cards: inBucket,
      // The bucket's scope question covers the scope-bearing verbs it holds.
      scopedVerbs: [...new Set(
        inBucket.flatMap((c) => c.grants).filter(isScoped).map((g) => g.verb),
      )].sort(),
    });
  }
  return out;
}

/** Total cards a grouping renders — the number the header quotes. */
export const cardCount = (buckets) => buckets.reduce((n, b) => n + b.cards.length, 0);
